import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, AlertTriangle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ExternalLink, User, Award, Check, AlertCircle, X, Search, GraduationCap, Briefcase, Tags, CheckCircle2, XCircle, Zap, Heart, Info } from "lucide-react";
import strings from "../../../utils/Strings";
import JobSearchClient, { JobSearchHistory, JobSearchResultCandidate, ExperienceTimelineItem, EducationTimelineItem, ResumeSearchSummary, SkillMatchDetail } from "../data/api/JobSearchClient";
import Timeline, { TimelineEntry } from "../../../shared/components/Timeline";
import { getResumePreviewUrl, getResumeDownloadUrl, downloadBulkResumes } from "../../../services/resumeFileService";
import ResumeViewer, { ResumeZoomControls, ResumeViewerHandle } from "../../../shared/components/ResumeViewer";
import Loader from "../../../shared/components/Loader";
import Modal from "../../../shared/components/Modal";
import JobSearchLoader from "../components/JobSearchLoader";
import { ApplicantSelectionModal } from "../components/ApplicantSelectionModal";
import { Calendar } from "lucide-react";
import "../components/styles/JobSearchStyles.css";

const getInitials = (firstName: string, lastName: string) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";

const hasProficiencyData = (c: JobSearchResultCandidate) =>
  c.proficiency_score != null &&
  (Number(c.proficiency_score) > 0 || (c.proficiency_reason ?? "").trim() !== "");

const getScoreColor = (score: number) => {
  if (score >= 75) return "#059669";
  if (score >= 50) return "#d97706";
  return "#dc2626";
};

const getScoreBg = (score: number) => {
  if (score >= 75) return "#f0fdf4";
  if (score >= 50) return "#fffbeb";
  return "#fef2f2";
};

const getScoreBorder = (score: number) => {
  if (score >= 75) return "#bbf7d0";
  if (score >= 50) return "#fde68a";
  return "#fecaca";
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length >= 5) {
    return `${parts[0]}-${parts[1]}-${parts[2]} ${parts[3]}:${parts[4]}${parts[5] ? ":" + parts[5] : ""}`;
  }
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return dateStr;
  }
};

// ─── Skill Match & Score helpers ──────────────────────────────────────────────
const formatTimelineDate = (date?: string) => {
  if (!date) return "";
  const val = String(date).trim();
  if (/present/i.test(val)) return "Present";
  if (/expected/i.test(val)) return val;
  const m = val.match(/^(\d{4})-(\d{2})/) || val.match(/^(\d{4})\/(\d{1,2})/);
  if (m) {
    const month = new Date(0, parseInt(m[2], 10) - 1).toLocaleString(undefined, { month: "short" });
    return `${month} ${m[1]}`;
  }
  return val;
};

const buildExperienceEntries = (items?: ExperienceTimelineItem[]): TimelineEntry[] => {
  return (items || []).map((x) => {
    const bullets = [...(x.responsibilities || []), ...(x.impact_metrics || [])].filter(Boolean);
    const range = [formatTimelineDate(x.startDate) || "?", x.endDate ? formatTimelineDate(x.endDate) : "Present"];
    return {
      title: x.role || "Role",
      subtitle: x.organization,
      dateRange: `${range[0]} → ${range[1]}`,
      badge: x.type ? (x.type.charAt(0).toUpperCase() + x.type.slice(1)) : undefined,
      badgeAccent: x.type === "current" ? "green" : "neutral",
      bullets,
    };
  });
};

const buildEducationEntries = (items?: EducationTimelineItem[]): TimelineEntry[] => {
  return (items || []).map((item) => {
    const titleParts = [item.degree, item.institution].filter(Boolean);
    const range = [formatTimelineDate(item.startDate), formatTimelineDate(item.endDate)].filter(Boolean);
    return {
      title: titleParts.join(", "),
      subtitle: item.score ? `Score: ${item.score}` : undefined,
      dateRange: range.length ? range.join(" → ") : undefined,
    };
  });
};

const MatchChips: React.FC<{ matched?: string[]; missing?: string[]; skillMatches?: SkillMatchDetail[] }> = ({ matched, missing, skillMatches }) => {
  const skillStatusChips = (skillMatches || []).length > 0;

  if (skillStatusChips) {
    return (
      <div className="js-match-chips">
        {skillMatches!.map((s, i) => {
          if (s.match_status === "perfect") {
            return <span key={i} className="js-match-chip matched" title={s.reason}><CheckCircle2 size={12} />{s.skill}</span>;
          }
          if (s.match_status === "partial") {
            return <span key={i} className="js-match-chip partial" title={s.reason}><CheckCircle2 size={12} />{s.skill}</span>;
          }
          if (s.match_status === "mentioned") {
            return <span key={i} className="js-match-chip mentioned" title={s.reason}><Tags size={12} />{s.skill}</span>;
          }
          return <span key={i} className="js-match-chip missing" title={s.reason}><XCircle size={12} />{s.skill}</span>;
        })}
      </div>
    );
  }

  const has = (matched?.length ?? 0) > 0 || (missing?.length ?? 0) > 0;
  return (
    <div className="js-match-chips">
      {(matched || []).map((s, i) => (
        <span key={`m-${i}`} className="js-match-chip matched"><CheckCircle2 size={12} />{s}</span>
      ))}
      {(missing || []).map((s, i) => (
        <span key={`x-${i}`} className="js-match-chip missing"><XCircle size={12} />{s}</span>
      ))}
      {!has && <span className="js-no-resume">No skill-match data available.</span>}
    </div>
  );
};

// ─── Candidate Card ───────────────────────────────────────────────────────────
interface CandidateCardProps {
  candidate: JobSearchResultCandidate;
  summary?: ResumeSearchSummary;
  index: number;
  onViewResume: (url: string) => void;
  onViewDetails: (candidate: JobSearchResultCandidate) => void;
  isTop: boolean;
}

const CandidateCard: React.FC<CandidateCardProps> = ({ candidate, summary, index, onViewResume, onViewDetails, isTop }) => {
  const [expanded, setExpanded] = useState(false);
  const score = parseFloat(String(candidate.match_score)) || 0;
  const highlight = summary?.top_candidates_highlights?.find((h) =>
    h.candidate_id === candidate.candidate_id
  );
  const whyText = isTop ? highlight?.why_match : candidate.relevance_reason;

  return (
    <motion.div
      className="js-candidate-card"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.06 }}
      onClick={() => onViewDetails(candidate)}
    >
      <div className="js-ccard-top">
        <div className="js-ccard-left">
          <div className="js-ccard-avatar" style={{ background: `hsl(${(index * 67) % 360}, 60%, 50%)` }}>
            {getInitials(candidate.first_name, candidate.last_name)}
          </div>
          <div>
            <div className="js-ccard-name">{candidate.first_name} {candidate.last_name}</div>
            <div className="js-ccard-email">{candidate.email_address}</div>
          </div>
        </div>
        <div className="js-ccard-right">
          <div
            className="js-ccard-score"
            style={{ color: getScoreColor(score), background: getScoreBg(score), border: `1px solid ${getScoreBorder(score)}` }}
          >
            {score.toFixed(2)}%
            <span className="js-score-label">MATCH SCORE</span>
          </div>
        </div>
      </div>

      <div className="js-ccard-match-row">
        {candidate.year_of_experience !== null && candidate.year_of_experience !== undefined && (
          <span className="js-ccard-exp">
            <Briefcase size={12} /> {candidate.year_of_experience} yr{candidate.year_of_experience === 1 ? "" : "s"} exp
          </span>
        )}
        {hasProficiencyData(candidate) && (
          <span className="js-ccard-exp js-ccard-prof" title={candidate.proficiency_reason}>
            <Zap size={12} /> {Math.round(Number(candidate.proficiency_score))}% skill proficiency
          </span>
        )}
      </div>

      {/* <MatchChips matched={candidate.matched_skills} missing={candidate.missing_skills} skillMatches={candidate.skill_matches} /> */}

      {/* {isTop && highlight?.key_qualifications && (
        <div className="js-ccard-quals">
          <span className="js-quals-label">QUALIFICATIONS &amp; TECH STACK</span>
          <div className="js-tag-row">
            {highlight.key_qualifications.map((q, i) => (
              <span key={i} className="js-tag js-tag-blue">{q}</span>
            ))}
          </div>
        </div>
      )} */}

      <div className="js-ccard-footer">
        {candidate.resume_url ? (
          <a href={candidate.resume_url} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewResume(candidate.resume_url); }} className="js-resume-link">
            <ExternalLink size={14} />
            View Resume
          </a>
        ) : (
          <span className="js-no-resume">No resume available</span>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button className="js-detail-toggle" onClick={(e) => { e.stopPropagation(); onViewDetails(candidate); }}>
            <Award size={14} /> Score Details
          </button>
          {whyText && (
            <button className="js-why-match-btn" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
              Why Match?
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && whyText && (
          <motion.div
            className="js-why-match-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="js-ai-reasoning">
              <span className="js-ai-label">AI REASONING</span>
              <p>{whyText}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Skill Legend ─────────────────────────────────────────────────────────────
const SkillLegend: React.FC = () => (
  <div className="js-skill-legend">
    <span className="js-legend-item js-legend-perfect"><CheckCircle2 size={14} /> Currently using it (latest role / projects)</span>
    <span className="js-legend-item js-legend-partial"><CheckCircle2 size={14} /> Used it before / some experience</span>
    <span className="js-legend-item js-legend-mentioned"><Tags size={14} /> Listed on resume (no proven usage)</span>
    <span className="js-legend-item js-legend-no"><XCircle size={14} /> No knowledge</span>
  </div>
);

// ─── Score Details Modal ──────────────────────────────────────────────────────
export interface CandidateDetailModalProps {
  candidate: any;
  index: number;
  total: number;
  onNavigate: (index: number) => void;
  onClose: () => void;
  isInline?: boolean;
  jobDescription?: string;
}

export const CandidateDetailModal: React.FC<CandidateDetailModalProps> = ({ candidate, index, total, onNavigate, onClose, isInline, jobDescription }) => {
  const [detailTab, setDetailTab] = useState<"skills" | "experience" | "education">("skills");
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const score = parseFloat(String(candidate.match_score)) || 0;
  const expEntries = buildExperienceEntries(candidate.experience_timeline);
  const eduEntries = buildEducationEntries(candidate.education_timeline);

  const activeReason =
    (detailTab === "skills" && (candidate.proficiency_reason || candidate.skills_reason)) ||
    (detailTab === "experience" && candidate.experience_reason) ||
    (detailTab === "education" && candidate.education_reason);

  const modalContent = (
      <div
        style={
          isInline 
          ? { margin: '-30px -30px -60px -30px', background: 'transparent', minHeight: '100vh', display: 'flex', flexDirection: 'column' }
          : { background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '680px', height: 'auto', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }
        }
        onClick={e => isInline ? undefined : e.stopPropagation()}
      >
        <div style={{ padding: isInline ? '0 24px' : '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: isInline ? 'none' : '1px solid #e2e8f0', background: isInline ? 'transparent' : '#f8fafc', flexShrink: 0, borderRadius: isInline ? '0' : '20px 20px 0 0', marginTop: isInline ? '20px' : '0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#dcfce7', color: '#065f46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px' }}>
              {getInitials(candidate.first_name, candidate.last_name)}
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>{candidate.first_name} {candidate.last_name}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{candidate.email_address}</div>
            </div>
          </div>
          {isInline ? (
            <div onClick={onClose} style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer', border: '1px solid #e2e8f0' }}>
              <X size={16} fill="#64748b" />
            </div>
          ) : (
            <button
              onClick={onClose}
              style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
            >
              <X size={16} /> Close
            </button>
          )}
        </div>
        {isInline && <hr style={{ border: 'none', borderTop: '1px solid rgba(120, 90, 40, 0.15)', margin: '20px 24px' }} />}

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {isInline ? (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)', 
                borderRadius: '16px', 
                padding: '24px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                marginBottom: '24px',
                border: '1px solid #d1fae5',
                boxShadow: '0 10px 30px rgba(5, 150, 105, 0.08)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', right: '-10%', top: '-20%', width: '150px', height: '150px', background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', zIndex: 1 }}>
                  <div style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: `conic-gradient(#059669 ${score * 3.6}deg, #e5e7eb 0deg)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '16px', fontWeight: 800, color: '#065f46' }}>{Math.round(score)}%</span>
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#065f46', fontSize: '12px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Overall Match Score
                    </div>
                    <div style={{ color: '#0f172a', fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>
                      {score >= 80 ? 'Exceptional Fit' : score >= 60 ? 'Strong Potential' : 'Moderate Match'}
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => setShowScoreInfo(!showScoreInfo)}
                  style={{ position: 'relative', zIndex: 1, width: 36, height: 36, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
                >
                  <Info size={16} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                {hasProficiencyData(candidate) && (
                  <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(120,90,40,0.15)', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '8px' }}>PROFICIENCY</div>
                    <div style={{ fontSize: '18px', fontWeight: 800, color: '#065f46' }}>{Math.round(Number(candidate.proficiency_score) || 0)}%</div>
                  </div>
                )}
                <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(120,90,40,0.15)', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '8px' }}>EXPERIENCE</div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#065f46' }}>{Math.round(Number(candidate.experience_score) || 0)}%</div>
                </div>
                <div style={{ flex: 1, background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid rgba(120,90,40,0.15)', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', marginBottom: '8px' }}>EDUCATION</div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: '#065f46' }}>{Math.round(Number(candidate.education_score) || 0)}%</div>
                </div>
              </div>

              {candidate.year_of_experience !== null && candidate.year_of_experience !== undefined && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#e6f4ea', border: '1px solid #bbf7d0', padding: '8px 16px', borderRadius: '100px', color: '#065f46', fontSize: '14px', fontWeight: 600 }}>
                  <Briefcase size={14} /> {candidate.year_of_experience} yrs experience
                </div>
              )}
            </div>
          ) : (
            <div className="js-modal-score-head">
              <div className="js-ccard-score" style={{ color: getScoreColor(score), background: getScoreBg(score), border: `1px solid ${getScoreBorder(score)}` }}>
                {score.toFixed(2)}%
                <span className="js-score-label">OVERALL MATCH SCORE</span>
              </div>
              <span className="js-score-info-wrap">
                <span
                  className="js-score-info-btn"
                  onClick={() => setShowScoreInfo(!showScoreInfo)}
                  onMouseEnter={() => setShowScoreInfo(true)}
                  onMouseLeave={() => setShowScoreInfo(false)}
                >
                  <AlertCircle size={14} />
                </span>
                {showScoreInfo && (
                  <span className="js-score-tooltip">
                    Overall Match Score is the AI's combined assessment of a candidate's Skills, Experience, and
                    Education against the job description. The three category scores below show how each part contributes.
                  </span>
                )}
              </span>

              <div className="js-sub-scores">
                {hasProficiencyData(candidate) && (
                  <div className="js-sub-score">
                    <span className="js-sub-score-label">Proficiency</span>
                    <span className="js-sub-score-value" style={{ color: getScoreColor(candidate.proficiency_score ?? 0) }}>
                      {Math.round(Number(candidate.proficiency_score) || 0)}%
                    </span>
                  </div>
                )}
                <div className="js-sub-score">
                  <span className="js-sub-score-label">Experience</span>
                  <span className="js-sub-score-value" style={{ color: getScoreColor(candidate.experience_score ?? 0) }}>
                    {Math.round(Number(candidate.experience_score) || 0)}%
                  </span>
                </div>
                <div className="js-sub-score">
                  <span className="js-sub-score-label">Education</span>
                  <span className="js-sub-score-value" style={{ color: getScoreColor(candidate.education_score ?? 0) }}>
                    {Math.round(Number(candidate.education_score) || 0)}%
                  </span>
                </div>
              </div>

              {candidate.year_of_experience !== null && candidate.year_of_experience !== undefined && (
                <span className="js-ccard-exp">
                  <Briefcase size={12} /> {candidate.year_of_experience} yr{candidate.year_of_experience === 1 ? "" : "s"} exp
                </span>
              )}
            </div>
          )}

          {jobDescription && (
            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Job Description</div>
              <div style={{ fontSize: '13px', color: '#334155', background: isInline ? '#fff' : '#f8fafc', padding: '16px', borderRadius: '12px', border: isInline ? '1px solid rgba(120,90,40,0.15)' : '1px solid #e2e8f0', whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: '200px', overflowY: 'auto' }}>
                {jobDescription}
              </div>
            </div>
          )}

          {isInline ? (
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(120,90,40,0.15)', marginTop: '16px' }}>
              <button 
                onClick={() => setDetailTab("skills")} 
                style={{ flex: 1, padding: '10px 0', background: 'transparent', border: 'none', borderBottom: detailTab === 'skills' ? '2px solid #065f46' : '2px solid transparent', color: detailTab === 'skills' ? '#065f46' : '#94a3b8', fontWeight: detailTab === 'skills' ? 700 : 600, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <Tags size={14} /> Skills
              </button>
              <button 
                onClick={() => setDetailTab("experience")} 
                style={{ flex: 1, padding: '10px 0', background: 'transparent', border: 'none', borderBottom: detailTab === 'experience' ? '2px solid #065f46' : '2px solid transparent', color: detailTab === 'experience' ? '#065f46' : '#94a3b8', fontWeight: detailTab === 'experience' ? 700 : 600, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <Briefcase size={14} /> Experience
              </button>
              <button 
                onClick={() => setDetailTab("education")} 
                style={{ flex: 1, padding: '10px 0', background: 'transparent', border: 'none', borderBottom: detailTab === 'education' ? '2px solid #065f46' : '2px solid transparent', color: detailTab === 'education' ? '#065f46' : '#94a3b8', fontWeight: detailTab === 'education' ? 700 : 600, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <GraduationCap size={14} /> Education
              </button>
            </div>
          ) : (
            <div className="js-detail-tabs">
              <button className={`js-detail-tab ${detailTab === "skills" ? "active" : ""}`} onClick={() => setDetailTab("skills")}>
                <Tags size={14} /> Skills
              </button>
              <button className={`js-detail-tab ${detailTab === "experience" ? "active" : ""}`} onClick={() => setDetailTab("experience")}>
                <Briefcase size={14} /> Experience
              </button>
              <button className={`js-detail-tab ${detailTab === "education" ? "active" : ""}`} onClick={() => setDetailTab("education")}>
                <GraduationCap size={14} /> Education
              </button>
            </div>
          )}

          {activeReason && (
            isInline ? (
              <div style={{ background: '#fff', borderRadius: '12px', padding: '16px 20px', borderLeft: '4px solid #ea580c', marginTop: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', marginBottom: '20px' }}>
                <div style={{ color: '#ea580c', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '6px', textTransform: 'uppercase' }}>
                  {detailTab === "skills" ? "AI SKILLS ASSESSMENT" : detailTab === "experience" ? "AI EXPERIENCE ASSESSMENT" : "AI EDUCATION ASSESSMENT"}
                </div>
                <div style={{ color: '#334155', fontSize: '14px', fontStyle: 'italic', lineHeight: 1.6 }}>
                  {activeReason}
                </div>
              </div>
            ) : (
              <div className="js-detail-ai">
                <span className="js-ai-label">{detailTab === "skills" ? "AI SKILLS ASSESSMENT" : detailTab === "experience" ? "AI EXPERIENCE ASSESSMENT" : "AI EDUCATION ASSESSMENT"}</span>
                <p>{activeReason}</p>
              </div>
            )
          )}

          {detailTab === "skills" && (
            <div>
              <SkillLegend />
              <MatchChips matched={candidate.matched_skills} missing={candidate.missing_skills} skillMatches={candidate.skill_matches} />
            </div>
          )}
          {detailTab === "experience" && (
            <div>
              <Timeline entries={expEntries} emptyText="No professional experience on resume." />
            </div>
          )}
          {detailTab === "education" && (
            <div>
              <Timeline entries={eduEntries} emptyText="No education data on resume." />
            </div>
          )}
        </div>

        {/* Navigation footer */}
        {!isInline && (
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, borderRadius: '0 0 20px 20px' }}>
            <button
              onClick={() => onNavigate(index - 1)}
              disabled={index <= 0}
              style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: index <= 0 ? '#cbd5e1' : '#334155', cursor: index <= 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '8px 14px', borderRadius: '8px' }}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>
              {index + 1} of {total}
            </span>
            <button
              onClick={() => onNavigate(index + 1)}
              disabled={index >= total - 1}
              style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: index >= total - 1 ? '#cbd5e1' : '#334155', cursor: index >= total - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '8px 14px', borderRadius: '8px' }}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    );
    
    if (isInline) {
      return modalContent;
    }

    return ReactDOM.createPortal(
      <div
        style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px' }}
        onClick={onClose}
      >
        {modalContent}
      </div>,
      document.body
    );
};


// ─── Main Page ────────────────────────────────────────────────────────────────
const JobSearchResultPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState<JobSearchHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenProgressPercent, setRegenProgressPercent] = useState(0);
  const [regenProgressMessage, setRegenProgressMessage] = useState("");
  const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [resumeZoom, setResumeZoom] = useState(1.5);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const resumeViewerRef = useRef<ResumeViewerHandle>(null);
  const [jdExpanded, setJdExpanded] = useState(false);
  const [jdOverflows, setJdOverflows] = useState(false);
  const [activeTab, setActiveTab] = useState<'top' | 'relevant' | 'overview'>('top');
  const [isApplicantModalOpen, setIsApplicantModalOpen] = useState(false);
  const [applicantModalMode, setApplicantModalMode] = useState<'schedule' | 'download'>('schedule');
  const jdRef = useRef<HTMLParagraphElement>(null);

  const openResumePreview = async (url: string | null | undefined) => {
    setPdfLoading(true);
    const inlineUrl = await getResumePreviewUrl(url);
    setResumePreviewUrl(inlineUrl);
  };

  const fetchSearch = async (searchId: string) => {
    setLoading(true);
    try {
      const data = await JobSearchClient.getJobSearch(searchId);
      setSearch(data);
    } catch (err: any) {
      setError(err.message || "Failed to load talent search results");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchSearch(id);
  }, [id]);

  // Detect if JD text overflows 2 lines
  useEffect(() => {
    if (!jdRef.current) return;
    const el = jdRef.current;
    // Temporarily remove clamp to measure full height
    el.style.webkitLineClamp = 'unset';
    el.style.overflow = 'visible';
    const fullHeight = el.scrollHeight;
    // Re-apply 2-line clamp to measure clamped height
    el.style.webkitLineClamp = '2';
    el.style.overflow = 'hidden';
    const clampedHeight = el.clientHeight;
    setJdOverflows(fullHeight > clampedHeight + 2);
    // Restore correct display state
    if (!jdExpanded) {
      el.style.webkitLineClamp = '2';
    } else {
      el.style.webkitLineClamp = 'unset';
    }
  }, [search, jdExpanded]);

  const pollRegenerateStatus = async (searchId: string) => {
    try {
      const statusRes = await JobSearchClient.getJobSearchStatus(searchId);
      const currentStatus = statusRes.status?.toUpperCase();
      if (currentStatus === "COMPLETE" || currentStatus === "COMPLETED") {
        setRegenProgressPercent(100);
        setRegenProgressMessage("Finalizing Results...");
        const data = await JobSearchClient.getJobSearch(searchId);
        setTimeout(() => {
          setSearch(data);
          setRegenerating(false);
        }, 1500);
      } else if (currentStatus === "FAILED") {
        setError(statusRes.error || "Regeneration failed during processing.");
        setRegenerating(false);
      } else {
        // Still processing
        // Ignore stale 100% reads from DynamoDB eventual consistency during processing
        if (statusRes.progress_percent !== 100) {
          setRegenProgressPercent(statusRes.progress_percent || 0);
          setRegenProgressMessage(statusRes.progress_message || "");
        }
        setTimeout(() => pollRegenerateStatus(searchId), 2000);
      }
    } catch (err: any) {
      setError(err.message || "Failed to check regeneration status.");
      setRegenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!id) return;
    setRegenProgressPercent(0);
    setRegenProgressMessage("Initializing regeneration...");
    setRegenerating(true);
    setError(null);
    try {
      const data = await JobSearchClient.regenerateJobSearch(id);
      const currentStatus = data.status?.toUpperCase();
      if (currentStatus === "PROCESSING") {
        setRegenProgressPercent(0);
        setRegenProgressMessage("Initializing regeneration...");
        setTimeout(() => pollRegenerateStatus(id), 2000);
      } else if (currentStatus === "COMPLETE" || currentStatus === "COMPLETED") {
        setSearch(data);
        setRegenerating(false);
      } else {
        setError(data.error || "Regeneration failed.");
        setRegenerating(false);
      }
    } catch (err: any) {
      setError(err.message || "Failed to regenerate results");
      setRegenerating(false);
    }
  };

  if (loading) return <Loader isLoading={true} message="Loading results..." />;

  if (error || !search) {
    return (
      <div className="learner-page" style={{ overflowY: 'auto' }}>
        <div className="js-page-nav">
          <button className="js-nav-back-btn" onClick={() => navigate("/dashboard/job-search")}>
            <ArrowLeft size={18} />
          </button>
          <span className="js-nav-title">Talent Search Results</span>
        </div>
        <div style={{ marginTop: 24, color: 'var(--danger, #dc2626)', fontSize: '0.875rem' }}>{error || "Search not found"}</div>
      </div>
    );
  }

  const results = search.results?.results || [];
  const totalResults = parseInt(String(search.results?.total_results || "0")) || results.length;
  const summary = search.results?.generated_summary;

  return (
    <motion.div className="learner-page" style={{ overflowY: 'auto' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
      <Modal open={regenerating} title="" onClose={() => {}} width="560px">
        <JobSearchLoader 
          progressPercent={regenProgressPercent} 
          progressMessage={regenProgressMessage} 
          titleText="Regenerating Search" 
        />
      </Modal>

      {/* Back + Page title */}
      <div className="js-page-nav">
        <button className="js-nav-back-btn" onClick={() => navigate("/dashboard/job-search")}>
          <ArrowLeft size={18} />
        </button>
        <h1 className="js-nav-title">Talent Search Results</h1>
      </div>

      {/* ── Top Info Card (matches reference design) ── */}
      <div className="js-info-card">
        <div className="js-info-card-inner">
          <div className="js-info-card-left">
            <h1 className="js-info-title">{search.title}</h1>
            <div className="js-info-chips">
              <span className="js-chip">
                <User size={12} />
                {search.searched_by}
              </span>
              <span className="js-chip">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                {formatDate(search.search_date)}
              </span>
              <span className="js-chip js-chip-count">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                {totalResults} {totalResults === 1 ? "Result" : "Results"}
              </span>
            </div>
          </div>

          <div className="js-info-card-right" style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-primary" onClick={() => { setApplicantModalMode('schedule'); setIsApplicantModalOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '36px', padding: '0 16px', fontSize: '0.85rem' }}>
              <Calendar size={15} />
              Schedule Interview
            </button>
            <button className="btn-primary" onClick={() => { setApplicantModalMode('download'); setIsApplicantModalOpen(true); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '36px', padding: '0 16px', fontSize: '0.85rem', background: '#3b82f6', borderColor: '#3b82f6' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              Download Resumes
            </button>
            <button className="js-regen-btn" onClick={handleRegenerate} disabled={regenerating}>
              <RefreshCw size={15} className={regenerating ? "spin" : ""} />
              Regenerate
            </button>
          </div>
        </div>

        {/* Full-width divider */}
        <hr className="js-info-divider" />

        {/* JD preview (full width below divider) */}
        <p
          ref={jdRef}
          className="js-info-jd"
          style={{
            WebkitLineClamp: jdExpanded ? 'unset' : 2,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {search.job_description}
        </p>
        {jdOverflows && (
          <button className="js-jd-toggle" onClick={() => setJdExpanded(!jdExpanded)}>
            {jdExpanded ? "Show Less ↑" : "Show More ↓"}
          </button>
        )}
      </div>

      {/* ── Content ── */}
      {results.length === 0 ? (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg, 16px)',
          padding: '32px 24px',
          textAlign: 'center',
          marginTop: '16px',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'rgba(5, 150, 105, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '16px',
            border: '1px solid rgba(5, 150, 105, 0.2)'
          }}>
            <Search size={24} color="var(--primary)" />
          </div>

          <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: '12px' }}>
            {strings.jobSearch.noResults || "No Candidates Found"}
          </h3>

          <div style={{
            background: 'var(--background)',
            padding: '12px 16px',
            borderRadius: 'var(--radius-md, 12px)',
            maxWidth: '600px',
            border: '1px solid var(--border)',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', margin: 0, lineHeight: '1.5' }}>
              {search.results?.message || search.message || "We couldn't find any suitable candidates matching this job description. Try adjusting your search criteria or uploading more resumes."}
            </p>
          </div>

          <button
            className="btn-primary"
            style={{ width: 'fit-content', padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}
            onClick={() => navigate("/dashboard/job-search")}
          >
            <ArrowLeft size={16} /> Back to Search
          </button>
        </div>
      ) : (
        <>
          {/* ── Tabs ── */}
          <div className="js-tabs-container" style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            {(() => {
              const topCandidateIds = summary?.top_candidates_highlights?.map(h => h.candidate_id) || [];
              const topCandidates = results.filter(c => topCandidateIds.includes(c.candidate_id));
              const relevantCandidates = results.filter(c => !topCandidateIds.includes(c.candidate_id));
              const tabBtnStyle = (tab: string) => ({
                padding: '10px 20px',
                borderRadius: '20px',
                background: activeTab === tab ? 'var(--primary)' : 'var(--surface)',
                color: activeTab === tab ? '#fff' : 'var(--text-heading)',
                border: activeTab === tab ? 'none' : '1px solid var(--border)',
                cursor: 'pointer' as const,
                fontSize: '14px',
                fontWeight: activeTab === tab ? 700 : 500,
                outline: 'none' as const,
                transition: 'all 0.2s'
              });
              return (
                <>
                  <button onClick={() => setActiveTab('top')} style={tabBtnStyle('top')}>
                    Top Applicants ({topCandidates.length})
                  </button>
                  <button onClick={() => setActiveTab('relevant')} style={tabBtnStyle('relevant')}>
                    Relevant Applicants ({relevantCandidates.length})
                  </button>
                  <button onClick={() => setActiveTab('overview')} style={tabBtnStyle('overview')}>
                    Overview
                  </button>
                </>
              );
            })()}
          </div>

          {activeTab === 'overview' && (
            <div>
              {summary?.recommendation && (
                <div className="js-insight-banner">
                  <div className="js-insight-icon"><AlertTriangle size={18} /></div>
                  <div>
                    <div className="js-insight-title">AI RECRUITER INSIGHT</div>
                    <div className="js-insight-text">{summary.recommendation}</div>
                  </div>
                </div>
              )}

              {summary && (
                <div className="js-assessment-card">
                  <div className="js-assess-header">
                    <h3 className="js-assess-title">Overall Assessment</h3>
                  </div>
                  <p className="js-assess-text">{summary.overall_assessment}</p>

                  <div className="js-assess-row-sections">
                    {summary.experience_levels && (
                      <div className="js-assess-section">
                        <div className="js-assess-section-label exp-label">
                          <span className="js-label-dot exp-dot"></span>
                          EXPERIENCE LEVEL
                        </div>
                        <div className="js-exp-pill-row">
                          <span className="js-exp-icon">🎓</span>
                          <span className="js-exp-text">{summary.experience_levels}</span>
                        </div>
                      </div>
                    )}

                    <div className="js-assess-lists-row">
                      {summary.key_strengths_found?.length > 0 && (
                        <div className="js-assess-list-col">
                          <div className="js-list-box green-box">
                            <div className="js-list-box-title green-text">
                              <Award size={18} />
                              Key Strengths
                            </div>
                            <div className="js-list-box-items">
                              {summary.key_strengths_found.map((s, i) => (
                                <div key={i} className="js-list-item">
                                  <Check size={16} className="js-item-icon green-text" />
                                  <span>{s}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {summary.skill_gaps?.length > 0 && (
                        <div className="js-assess-list-col">
                          <div className="js-list-box red-box">
                            <div className="js-list-box-title red-text">
                              <AlertCircle size={18} />
                              Missing Skills
                            </div>
                            <div className="js-list-box-items">
                              {summary.skill_gaps.map((g, i) => (
                                <div key={i} className="js-list-item">
                                  <X size={16} className="js-item-icon red-text" />
                                  <span>{g}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(activeTab === 'top' || activeTab === 'relevant') && (
            <div>
              {(() => {
                const topCandidateIds = summary?.top_candidates_highlights?.map(h => h.candidate_id) || [];
                const topCandidates = results.filter(c => topCandidateIds.includes(c.candidate_id));
                const relevantCandidates = results.filter(c => !topCandidateIds.includes(c.candidate_id));
                const isTop = activeTab === 'top';
                const candidates = isTop ? topCandidates : relevantCandidates;

                return (
                  <div className="js-candidate-list">
                    {candidates.length > 0 ? (
                      candidates.map((candidate, i) => (
                        <CandidateCard key={candidate.candidate_id} candidate={candidate} summary={summary} index={i} isTop={isTop} onViewResume={(url) => { openResumePreview(url); }} onViewDetails={(c) => setDetailIndex(results.findIndex(r => r.candidate_id === c.candidate_id))} />
                      ))
                    ) : (
                      <div style={{ gridColumn: '1 / -1', background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg, 16px)', padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', margin: '20px 0' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'var(--surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', color: 'var(--text-muted)' }}>
                          {isTop ? <Award size={20} /> : <User size={20} />}
                        </div>
                        <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-heading)', margin: '0 0 8px 0' }}>
                          No {isTop ? 'Top' : 'Relevant'} Applicants Found
                        </h4>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, maxWidth: '400px' }}>
                          {isTop
                            ? 'None of the candidates matched the specific high-level criteria for the top applicants category.'
                            : "We couldn't find any additional candidates that fit the relevant category for this search."}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* ── Score Details Modal ── */}
      {detailIndex !== null && detailIndex >= 0 && detailIndex < results.length && (
        <CandidateDetailModal
          candidate={results[detailIndex]}
          index={detailIndex}
          total={results.length}
          onNavigate={(i) => setDetailIndex(i)}
          onClose={() => setDetailIndex(null)}
        />
      )}

      {/* ── Resume PDF Viewer Modal ── */}
      {resumePreviewUrl && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px 80px' }}
          onClick={() => { setResumePreviewUrl(null); setPdfLoading(false); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>Resume Preview</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResumeZoomControls 
                  zoom={resumeZoom} 
                  onZoomChange={setResumeZoom} 
                  onSearchClick={() => resumeViewerRef.current?.openSearch()} 
                  onDownloadClick={async () => {
                    const downloadUrl = await getResumeDownloadUrl(resumePreviewUrl);
                    if (downloadUrl) {
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = "resume";
                      a.click();
                    }
                  }}
                />
                <button
                  onClick={() => { setResumePreviewUrl(null); setPdfLoading(false); }}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              {pdfLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 1 }}>
                  <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>Loading PDF...</span>
                </div>
              )}
              <ResumeViewer
                key={resumePreviewUrl}
                ref={resumeViewerRef}
                url={resumePreviewUrl}
                zoom={resumeZoom}
                onZoomChange={setResumeZoom}
                onLoaded={() => setPdfLoading(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      <ApplicantSelectionModal
        isOpen={isApplicantModalOpen}
        onClose={() => setIsApplicantModalOpen(false)}
        candidates={results}
        mode={applicantModalMode}
        onProceed={async (selectedEmails) => {
          if (applicantModalMode === 'schedule') {
            setIsApplicantModalOpen(false);
            navigate('/dashboard/interviews/schedule', { state: { selectedEmails: selectedEmails.join(',') } });
          } else if (applicantModalMode === 'download') {
            try {
              // We'll let the modal show its own loading state instead of taking over the full page UI
              await downloadBulkResumes(selectedEmails);
              setIsApplicantModalOpen(false);
            } catch (err) {
              setError("Failed to download resumes");
            }
          }
        }}
      />
    </motion.div>
  );
};

export default JobSearchResultPage;
